import {
  type Color,
  type ColorScheme,
  resolveColor,
} from '@blocksuite/affine-model';
import { html, nothing, svg, type TemplateResult } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

const brushStrokeMaskUrl = new URL(
  './horizontal-brush-stroke.assets.svg',
  import.meta.url
).href;

type BrushHeadingVisualOptions = {
  background: string;
  foreground: string;
  fontSize?: number;
  height: number | string;
  maskId: string;
  placeholder?: boolean;
  text?: string;
  width: number | string;
};

export const brushHeadingTextStyle = (
  foreground: string,
  placeholder = false,
  fontSize = 21
) =>
  ({
    alignItems: 'center',
    boxSizing: 'border-box',
    color: foreground,
    display: 'flex',
    fontFamily: "Kalam, 'Comic Sans MS', cursive",
    fontSize: `${fontSize}px`,
    fontStyle: 'normal',
    fontWeight: '600',
    inset: '0 6%',
    justifyContent: 'center',
    letterSpacing: '0.015em',
    lineHeight: '1.32',
    margin: '0',
    opacity: placeholder ? '0.62' : '1',
    padding: '0',
    position: 'absolute',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  }) as const;

const COLOR_STYLES: Record<string, { background: string; foreground: string }> =
  {
    beige: { background: '#d4bf91', foreground: '#2f2a20' },
    green: { background: '#587760', foreground: '#fffdf5' },
    turquoise: { background: '#328d87', foreground: '#fffdf8' },
    red: { background: '#b84a42', foreground: '#fffdf8' },
  };

function foregroundFor(background: string) {
  const hex = background.match(/^#([\da-f]{6})$/i)?.[1];
  if (!hex) return '#2f2a20';
  const channels = [0, 2, 4].map(offset =>
    Number.parseInt(hex.slice(offset, offset + 2), 16)
  );
  const luminance =
    (channels[0] * 0.299 + channels[1] * 0.587 + channels[2] * 0.114) / 255;
  return luminance < 0.52 ? '#fffdf8' : '#2f2a20';
}

export function resolveBrushHeadingColors(
  background: Color,
  textColor: Color,
  theme: ColorScheme
) {
  const resolvedBackground = resolveColor(background, theme);
  const preset = COLOR_STYLES[resolvedBackground];
  const fallbackForeground =
    preset?.foreground ?? foregroundFor(resolvedBackground);
  return {
    background: preset?.background ?? resolvedBackground,
    foreground: textColor
      ? resolveColor(textColor, theme, fallbackForeground)
      : fallbackForeground,
  };
}

/**
 * Shared visual for the canvas block, toolbar miniature, and drag preview.
 * The silhouette is built from overlapping paint deposits and sparse bristle
 * gaps instead of decorative lines or a geometric ribbon edge.
 */
export function renderBrushHeadingVisual({
  background,
  foreground,
  fontSize = 21,
  height,
  maskId,
  placeholder = false,
  text,
  width,
}: BrushHeadingVisualOptions): TemplateResult {
  const cssLength = (value: number | string) =>
    typeof value === 'number' ? `${value}px` : value;

  return html`<div
    class="brush-heading-visual"
    style=${styleMap({
      height: cssLength(height),
      position: 'relative',
      width: cssLength(width),
    })}
  >
    <div
      class="brush-heading-paint"
      style=${styleMap({
        display: 'flex',
        height: '100%',
        inset: '0',
        overflow: 'hidden',
        position: 'absolute',
        transform: 'rotate(180deg)',
        transformOrigin: 'center',
        width: '100%',
      })}
      aria-hidden="true"
    >
      ${svg`<svg
        height="100%"
        viewBox="35 321 130 128"
        preserveAspectRatio="none"
        style="aspect-ratio: 130 / 128; flex: 0 1 auto; margin-right: -1px; max-width: 22%; position: relative; z-index: 2;"
      >
        <defs>
          <mask id=${`${maskId}-left`} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse" mask-type="alpha" x="35" y="321" width="130" height="128">
            <image href=${brushStrokeMaskUrl} width="800" height="800"></image>
          </mask>
        </defs>
        <rect x="35" y="321" width="130" height="128" fill=${background} mask=${`url(#${maskId}-left)`}></rect>
      </svg>
      <svg
        height="100%"
        width="100%"
        viewBox="165 321 480 128"
        preserveAspectRatio="none"
        style="flex: 1 1 0; margin-right: -1px; min-width: 0; position: relative; z-index: 1;"
      >
        <defs>
          <mask id=${`${maskId}-middle`} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse" mask-type="alpha" x="165" y="321" width="480" height="128">
            <image href=${brushStrokeMaskUrl} width="800" height="800"></image>
          </mask>
        </defs>
        <rect x="165" y="321" width="480" height="128" fill=${background} mask=${`url(#${maskId}-middle)`}></rect>
      </svg>
      <svg
        height="100%"
        viewBox="645 321 134 128"
        preserveAspectRatio="none"
        style="aspect-ratio: 134 / 128; flex: 0 1 auto; max-width: 22%; position: relative;"
      >
        <defs>
          <mask id=${`${maskId}-right`} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse" mask-type="alpha" x="645" y="321" width="134" height="128">
            <image href=${brushStrokeMaskUrl} width="800" height="800"></image>
          </mask>
        </defs>
        <rect x="645" y="321" width="134" height="128" fill=${background} mask=${`url(#${maskId}-right)`}></rect>
      </svg>`}
    </div>
    ${text
      ? html`<span
          class="brush-heading-label"
          style=${styleMap(
            brushHeadingTextStyle(foreground, placeholder, fontSize)
          )}
          >${text}</span
        >`
      : nothing}
  </div>`;
}
